

import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ThumbsUp } from 'lucide-react';
import PlayFeedMiniCard from './PlayFeedMiniCard';
import PlayFeedChat from './PlayFeedChat';
import { supabase } from '../../../../../supabaseClient';

interface WatchProps {
  video: any;
  onBack: () => void;
  relatedVideos: any[];
  onSelectVideo?: (video: any) => void; // 👈 Adicionad para aceitar a troca de vídeo
}

// Função para calcular o "tempo atrás" (estilo YouTube)
const formatTimeAgo = (dateString?: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'há poucos segundos';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `há ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `há ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `há ${diffInMonths} mês${diffInMonths > 1 ? 'es' : ''}`;
  const diffInYears = Math.floor(diffInDays / 365);
  return `há ${diffInYears} ano${diffInYears > 1 ? 's' : ''}`;
};

export default function PlayFeedWatchView({ video, onBack, relatedVideos, onSelectVideo }: WatchProps) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Extrai o ID do YouTube de forma segura para o Embed funcionar perfeitamente
  const getEmbedUrl = (urlOrId: string, startAt: number) => {
    let baseUrl = '';
    if (!urlOrId) baseUrl = 'https://www.youtube.com/embed/dQw4w9WgXcQ';
    else if (urlOrId.length === 11) baseUrl = `https://www.youtube.com/embed/${urlOrId}`;
    else {
      const match = urlOrId.match(/(?:v=|youtu\.be\/)([^&]+)/);
      baseUrl = match ? `https://www.youtube.com/embed/${match[1]}` : `https://www.youtube.com/embed/${urlOrId}`;
    }
    return `${baseUrl}?start=${startAt}&enablejsapi=1`;
  };

  const videoSrc = video.embed_id || video.url || video.id;

 // Estados para likes e views baseados nas colunas corretas do Supabase
  const [likes, setLikes] = useState<number>(video.likes_count || 0);
  const [views, setViews] = useState<number>(video.views_count || 0);
  const [hasLiked, setHasLiked] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [startTime, setStartTime] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(true);

  

  // Efeito que conta +1 visualização assim que o vídeo é aberto
  useEffect(() => {
    const countView = async () => {
      const videoId = video.id || video._id;
      if (!videoId) return;
      
      const newViews = (video.views_count || 0) + 1;
      setViews(newViews); // Atualiza na tela
      
      // Salva no Supabase
      await supabase
        .from('videos')
        .update({ views_count: newViews  })
        .eq('id', videoId);
    };
    
    countView();
  }, [video]); // Executa sempre que um novo vídeo for selecionado

  const handleLike = async () => {
    if (hasLiked) return;
    const newLikes = likes + 1;
    setLikes(newLikes);
    setHasLiked(true);

    const videoId = video.id || video._id;
    await supabase
      .from('videos')
      .update({ likes_count: newLikes })
      .eq('id', videoId);
  };

  const handleShare = async () => {
    const videoId = video.id || video._id;
    // Cria um link direto apontando para o site com o parâmetro do vídeo
    const shareUrl = `${window.location.origin}/?v=${videoId}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link do vídeo copiado para a área de transferência!.');
    } catch (err) {
      console.error('Erro ao copiar link:', err);
    }
  };

 // 💾 MOTOR DE PROGRESSO LEVE (Igual à tela de Filmes/Games via localStorage)
  useEffect(() => {
    setLoadingProgress(true);
    const videoId = video.id || video._id;

    // 1. Busca instantaneamente o tempo salvo no navegador
    const savedTime = localStorage.getItem(`doisplay_progress_${videoId}`);
    setStartTime(savedTime ? Math.floor(parseFloat(savedTime)) : 0);
    setLoadingProgress(false);

    // 2. Ouvir o YouTube para salvar o tempo
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.youtube.com') return;
      try {
        const data = JSON.parse(event.data);
        if (data.event === 'infoDelivery' && data.info?.currentTime) {
          const currentTime = Math.floor(data.info.currentTime);
          
          // Se já viu mais de 5 segundos, vai guardando na memória do navegador
          if (currentTime > 5) {
            localStorage.setItem(`doisplay_progress_${videoId}`, currentTime.toString());
          }
        }
      } catch (e) {}
    };

    window.addEventListener('message', handleMessage);

    // Cutuca o iframe para ele nos enviar o tempo atual
    const interval = setInterval(() => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*');
      }
    }, 2000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(interval);
    };
  }, [video]);

  return (
    <div className="watch-scrollbar" style={{
      display: 'flex', 
      flexDirection: 'column',
      width: '100%', 
      height: 'calc(100vh - 80px)', 
      overflowY: 'auto', 
      backgroundColor: '#000',
      padding: isMobile ? '12px' : '20px 40px', 
      gap: '20px', 
      boxSizing: 'border-box',
      alignItems: 'center'
    }}>
      
      <style>
        {`
          .watch-scrollbar::-webkit-scrollbar { width: 8px; }
          .watch-scrollbar::-webkit-scrollbar-track { background: #000; }
          .watch-scrollbar::-webkit-scrollbar-thumb { background: #333; border-radius: 10px; }
          .watch-scrollbar::-webkit-scrollbar-thumb:hover { background: #E50914; }
        `}
      </style>

      {/* Botão Voltar fixo no topo no mobile para facilitar a navegação */}
      {isMobile && (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}>
          <button 
            onClick={onBack}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(229, 9, 20, 0.15)',
              border: '1px solid rgba(229, 9, 20, 0.4)', color: '#fff', width: '40px', height: '40px', borderRadius: '10px', 
              cursor: 'pointer'
            }}
            title="Voltar"
          >
            <ArrowLeft size={20} />
          </button>
        </div>
      )}
      
      {/* Bloco Centralizador */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        width: '100%',
        maxWidth: '1350px',
        gap: '30px',
        boxSizing: 'border-box'
      }}>

        {/* COLUNA ESQUERDA: Player + Detalhes + Chat */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          minWidth: 0,
          gap: '12px'
        }}>
          
          {/* Player de Vídeo Real do YouTube (Iframe) */}
          {/* Player de Vídeo Real do YouTube (Iframe) */}
          <div style={{
            width: '100%',
            aspectRatio: '16/9', 
            backgroundColor: '#0a0a0a', 
            borderRadius: '12px',
            overflow: 'hidden', 
            border: '1px solid rgba(255,255,255,0.08)', 
            boxShadow: '0 15px 30px rgba(0,0,0,0.8)'
          }}>
            {loadingProgress ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: '14px' }}>
                A recuperar o vídeo...
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                src={getEmbedUrl(videoSrc, startTime)}
                title={video.title}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>

          {/* Detalhes do Vídeo */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <h1 style={{ color: '#fff', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold', margin: 0, lineHeight: '1.3' }}>
              {video.title}
            </h1>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
             <span style={{ color: '#888', fontSize: '12px' }}>
  {views} visualizações • {formatTimeAgo(video.created_at)}
</span>
            <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={handleLike}
                  style={{ 
                    display: 'flex', alignItems: 'center', gap: '6px', 
                    background: hasLiked ? 'rgba(229, 9, 20, 0.2)' : 'rgba(255,255,255,0.08)', 
                    border: 'none', 
                    color: hasLiked ? '#E50914' : '#fff', 
                    padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', fontSize: '12px',
                    transition: 'all 0.2s'
                  }}
                >
                  <ThumbsUp size={14} fill={hasLiked ? '#E50914' : 'none'} color={hasLiked ? '#E50914' : '#fff'} /> 
                  {likes} Curtidas
                </button>
              
              </div>
            </div>
          </div>

          {/* Módulo de Chat */}
          {/* Módulo de Chat */}
{/* Módulo de Chat */}
          <PlayFeedChat key={video.id || video._id} videoId={video.id || video._id} />
        </div>

        {/* COLUNA DIREITA: Botão de Voltar (Desktop) + Próximos Vídeos */}
        <div style={{ 
          width: isMobile ? '100%' : '380px', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '12px', 
          flexShrink: 0 
        }}>
          
          {!isMobile && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <h3 style={{ color: '#fff', fontSize: '15px', fontWeight: 'bold', margin: 0 }}>Próximos Vídeos</h3>
              <button 
                onClick={onBack}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(229, 9, 20, 0.15)',
                  border: '1px solid rgba(229, 9, 20, 0.4)', color: '#fff', width: '32px', height: '32px', borderRadius: '8px', 
                  cursor: 'pointer', transition: 'background 0.2s'
                }}
                title="Voltar ao Feed"
              >
                <ArrowLeft size={16} />
              </button>
            </div>
          )}

          {isMobile && (
            <h3 style={{ color: '#fff', fontSize: '15px', fontWeight: 'bold', margin: '10px 0 0 0' }}>Próximos Vídeos</h3>
          )}

          {relatedVideos.map((item) => (
            <PlayFeedMiniCard 
              key={item.id || item._id} 
              video={item} 
              onSelect={(selectedVideo) => {
                if (onSelectVideo) {
                  onSelectVideo(selectedVideo);
                  
                  // 👇 AJUSTE 1: Faz a página rolar de volta para o topo (onde está o Player) quando escolhe outro vídeo
                  const scrollContainer = document.querySelector('.watch-scrollbar');
                  if (scrollContainer) {
                    scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }
              }} 
            />
          ))}
        </div>

      </div>
    </div>
  );
}